// Vendored from @el7ven/cookie-kit (src/core/index.js), not published to a
// registry we can npm-install from — see resources/frontend/js/features/
// cookie-consent.js for our own markup/UI wired on top of this. Only the
// consent-storage core is used here (no React/Vue/Web Component UI).
import { VERSION, getVersion, getVersionInfo, logVersion } from './version.js'
import { hasGPC } from './gpc.js'

const DEFAULT_CONFIG = {
  // Version and revision for consent invalidation
  version: 'v2',
  revision: 0,

  // GDPR compliance
  consentExpireDays: 365,
  auditLog: false,

  // Storage
  storageKey: 'cookie_consent',
  storageType: 'localStorage', // 'localStorage' | 'cookie' | 'both'

  // Mode
  mode: 'gdpr', // 'gdpr' | 'essential'

  // Auto show
  autoShow: true,

  // Debug
  debug: false,

  // Region (optional)
  region: null, // 'EU' | 'US' | 'OTHER'

  // Global Privacy Control (CPRA/Colorado/Connecticut etc.) — when the
  // browser sends this signal, auto-record it as an opt-out of non-required
  // categories instead of waiting for a banner click.
  respectGPC: true,
  
  // Categories
  categories: {
    necessary: {
      id: 'necessary',
      label: 'Necesare',
      description: 'Cookie-uri esențiale pentru funcționarea site-ului',
      required: true,
      disabled: true,
      enabled: true
    },
    analytics: {
      id: 'analytics',
      label: 'Analiză',
      description: 'Cookie-uri pentru analiza traficului și comportamentului utilizatorilor',
      required: false,
      disabled: false,
      enabled: false
    },
    marketing: {
      id: 'marketing',
      label: 'Marketing',
      description: 'Cookie-uri pentru publicitate personalizată',
      required: false,
      disabled: false,
      enabled: false
    },
    preferences: {
      id: 'preferences',
      label: 'Preferințe',
      description: 'Cookie-uri pentru salvarea preferințelor utilizatorului',
      required: false,
      disabled: false,
      enabled: false
    }
  },
  
  // Texts
  texts: {
    essential: {
      title: '🍪 Файлы Cookie',
      message: 'Мы используем необходимые файлы cookie для работы сайта. Никакой аналитики и маркетинга.',
      button: 'Принять'
    },
    gdpr: {
      title: 'Preferințe Cookie-uri',
      message: 'Folosim cookie-uri pentru a-ți oferi cea mai bună experiență. Alege ce tipuri de cookie-uri accepți.',
      buttons: {
        acceptAll: 'Acceptă toate',
        rejectAll: 'Respinge toate',
        acceptSelection: 'Acceptă selecția',
        settings: 'Setări'
      }
    },
    settings: {
      title: 'Setări Cookie-uri',
      tabs: {
        privacy: 'Confidențialitate'
      },
      buttons: {
        acceptAll: 'Acceptă toate',
        rejectAll: 'Respinge toate',
        acceptSelection: 'Salvează selecția'
      },
      links: {
        moreInfo: 'Mai multe informații',
        cookieDetails: 'Detalii cookie-uri'
      }
    }
  },
  
  // Policy links
  policies: {
    privacy: '/privacy-policy',
    cookies: '/cookie-policy'
  },
  
  // Display options
  display: {
    position: 'bottom-right', // 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center'
    backdrop: true,
    closeOnBackdrop: true
  },
  
  // Analytics providers (optional)
  analytics: null,
  
  // Script blocking (optional)
  scriptBlocking: {
    enabled: true,
    strategy: 'data-category' // 'data-category' | 'type-rewrite'
  }
}

function isPromiseLike(value) {
  return !!value && typeof value.then === 'function'
}

function createWebStorageAdapter(storageKey) {
  if (typeof window === 'undefined' || !window.localStorage) {
    const memory = new Map()
    return {
      getItem: key => memory.get(key) ?? null,
      setItem: (key, value) => memory.set(key, value),
      removeItem: key => memory.delete(key),
      defaultKey: storageKey
    }
  }

  return {
    getItem: key => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
    removeItem: key => window.localStorage.removeItem(key),
    defaultKey: storageKey
  }
}

function mergeConfig(base, extra) {
  // Unlike the upstream package, a supplied `categories` map REPLACES the
  // default one entirely rather than merging with it — our config always
  // passes the full, final category list (matching the Figma design's 4
  // categories), so merging would silently keep the package's own unused
  // defaults (e.g. "analytics") alongside ours.
  return {
    ...base,
    ...extra,
    categories: extra?.categories || base.categories
  }
}

function normalizeCategories(categories) {
  if (Array.isArray(categories)) {
    return categories.reduce((acc, cat) => {
      acc[cat.id] = cat
      return acc
    }, {})
  }
  return categories || {}
}

export class CookieKitCore {
  constructor(userConfig = {}) {
    this.config = mergeConfig(DEFAULT_CONFIG, userConfig)
    this.config.categories = normalizeCategories(this.config.categories)
    this.listeners = new Map()
    this.storage = this.config.storageAdapter || createWebStorageAdapter(this.config.storageKey)
    this._consentCache = { raw: undefined, data: undefined }
    this.gpcDetected = this.config.respectGPC && hasGPC()
    this._initializePublicAPI()
  }

  _initializePublicAPI() {
    if (typeof window === 'undefined') return
    window.CookieConsent = {
      get:           () => this.getConsent(),
      getConsent:    () => this.getConsent(),
      has:           (cat) => this.hasCategoryConsent(cat),
      acceptAll:     (src) => this.acceptAll(src),
      rejectAll:     (src) => this.rejectAll(src),
      acceptSelected:(ids, src) => this.acceptSelected(ids, src),
      reset:         () => this.clearConsent(),
      on:            (event, cb) => this.on(event, cb),
      off:           (event, cb) => this.off(event, cb),
      consentManager: this,
    }
  }

  getStorageKey() {
    return this.config.storageKey || this.storage.defaultKey || 'cookie_consent'
  }

  /**
   * Whether the browser is sending the Global Privacy Control signal
   * (and respectGPC hasn't been disabled in config).
   * @returns {boolean}
   */
  hasGlobalPrivacyControl() {
    return this.gpcDetected
  }

  /**
   * If GPC is signaled and there's no existing consent decision yet, record
   * an opt-out of all non-required categories automatically — GPC is a
   * legally valid opt-out request under CPRA/Colorado/Connecticut etc. and
   * must be honored without waiting for a banner click. No-ops if GPC isn't
   * signaled, respectGPC is off, or the user already has a saved decision
   * (an explicit choice, including one made after a prior auto-applied GPC
   * opt-out, is never overridden).
   * @returns {Promise<Object|null>} the recorded consent, or null if no-op
   */
  async autoApplyGPC() {
    if (!this.gpcDetected) return null
    if (this.getConsent()) return null

    const categories = {}
    Object.keys(this.config.categories).forEach(id => {
      if (this.config.categories[id]?.enabled === true) {
        categories[id] = !!this.config.categories[id].required
      }
    })

    const consent = await this.saveConsent(categories, 'gpc')
    this.emit('gpcApplied', { consent, categories })
    return consent
  }

  getConsent() {
    try {
      const raw = this.storage.getItem(this.getStorageKey())
      if (isPromiseLike(raw)) return null
      if (!raw) return null

      // storage.getItem is cheap, but JSON.parse + validation isn't worth
      // repeating on every call (hasConsent/hasCategoryConsent call this)
      // when the underlying record hasn't changed since last read. Expiry
      // is still re-checked every call since it changes with the clock,
      // not with the stored content.
      let data = this._consentCache.raw === raw ? this._consentCache.data : undefined
      if (data === undefined) {
        data = JSON.parse(raw)
        this._consentCache = { raw, data }
      }

      if (!data || typeof data !== 'object') return null
      // Ignore records written by a different engine sharing the same
      // storage key (e.g. ConsentManager) — their schemas aren't compatible.
      if (data.engine && data.engine !== 'cookie-kit-core') return null
      if (data.expiresAt && Date.now() > new Date(data.expiresAt).getTime()) return null
      const cfgVersion = this.config.configVersion || this.config.version
      if (cfgVersion && data.version !== cfgVersion) return null
      return data
    } catch {
      return null
    }
  }

  async getConsentAsync() {
    try {
      const raw = await this.storage.getItem(this.getStorageKey())
      if (!raw) return null
      const data = JSON.parse(raw)
      if (!data || typeof data !== 'object') return null
      if (data.engine && data.engine !== 'cookie-kit-core') return null
      if (data.expiresAt && Date.now() > new Date(data.expiresAt).getTime()) return null
      const cfgVersion = this.config.configVersion || this.config.version
      if (cfgVersion && data.version !== cfgVersion) return null
      return data
    } catch {
      return null
    }
  }

  hasConsent() {
    const consent = this.getConsent()
    return !!consent?.hasConsented
  }

  async hasConsentAsync() {
    const consent = await this.getConsentAsync()
    return !!consent?.hasConsented
  }

  hasCategoryConsent(categoryId) {
    const consent = this.getConsent()
    return consent?.categories?.[categoryId] === true
  }

  async hasCategoryConsentAsync(categoryId) {
    const consent = await this.getConsentAsync()
    return consent?.categories?.[categoryId] === true
  }

  getDefaultCategoriesState() {
    const next = {}
    Object.keys(this.config.categories).forEach(id => {
      const category = this.config.categories[id]
      next[id] = category?.required === true
    })
    return next
  }

  async saveConsent(categories, source = 'api') {
    const now = new Date()
    const expiresAt = new Date(now.getTime() + this.config.consentExpireDays * 24 * 60 * 60 * 1000)

    const normalized = this.getDefaultCategoriesState()
    Object.keys(categories || {}).forEach(id => {
      if (Object.prototype.hasOwnProperty.call(normalized, id)) {
        normalized[id] = !!categories[id] || this.config.categories[id]?.required === true
      }
    })

    const optionalIds = Object.keys(normalized).filter(id => !this.config.categories[id]?.required)
    const allOptionalAccepted = optionalIds.length > 0 && optionalIds.every(id => normalized[id] === true)
    const allOptionalRejected = optionalIds.length > 0 && optionalIds.every(id => normalized[id] === false)

    let status = 'custom'
    if (optionalIds.length === 0 || allOptionalAccepted) status = 'accepted'
    if (allOptionalRejected) status = 'rejected'

    const consent = {
      engine: 'cookie-kit-core',
      consentId: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      timestamp: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      status,
      source,
      hasConsented: true,
      categories: normalized,
      version: this.config.configVersion || this.config.version || '0.1.0'
    }

    await this.storage.setItem(this.getStorageKey(), JSON.stringify(consent))
    this.emit('consentChanged', { consent, categories: normalized })
    return consent
  }

  acceptAll(source = 'banner') {
    const categories = {}
    Object.keys(this.config.categories).forEach(id => {
      categories[id] = true
    })
    return this.saveConsent(categories, source)
  }

  rejectAll(source = 'banner') {
    const categories = {}
    Object.keys(this.config.categories).forEach(id => {
      categories[id] = !!this.config.categories[id]?.required
    })
    return this.saveConsent(categories, source)
  }

  acceptSelected(selectedIds = [], source = 'settings') {
    const selectedSet = new Set(selectedIds)
    const categories = {}
    Object.keys(this.config.categories).forEach(id => {
      categories[id] = selectedSet.has(id) || this.config.categories[id]?.required === true
    })
    return this.saveConsent(categories, source)
  }

  clearConsent() {
    this.storage.removeItem(this.getStorageKey())
    this.emit('consentCleared', {})
  }

  async clearConsentAsync() {
    await this.storage.removeItem(this.getStorageKey())
    this.emit('consentCleared', {})
  }

  on(event, callback) {
    const list = this.listeners.get(event) || []
    list.push(callback)
    this.listeners.set(event, list)
    return () => this.off(event, callback)
  }

  off(event, callback) {
    const list = this.listeners.get(event) || []
    this.listeners.set(event, list.filter(cb => cb !== callback))
  }

  emit(event, payload) {
    const list = this.listeners.get(event) || []
    list.forEach(cb => cb(payload))

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(`cookie-consent:${event}`, { detail: payload }))
    }
  }
}

export function createCookieKit(config = {}) {
  return new CookieKitCore(config)
}

export { DEFAULT_CONFIG, VERSION, getVersion, getVersionInfo, logVersion }
